/**
 * FullscreenMode.jsx — Mode Plein Écran de projection.
 *
 * Objectif : projeter la page de cours telle quelle sur vidéoprojecteur.
 * Contrairement au mode "slide par slide", on garde toute la page (images
 * agrandissables + texte pédagogique visible) et on scrolle normalement.
 *
 * Ce composant :
 *   • déclenche le vrai plein écran F11 navigateur (Fullscreen API)
 *   • masque la topbar sticky du cours pour dégager l'écran
 *   • affiche <ClassroomTools /> (Timer + Laser + Annotation) en overlay
 *   • se ferme automatiquement sur ESC natif
 *
 * Utilisation :
 *   const [fs, setFs] = useState(false);
 *   <Button onClick={() => enterFullscreen(() => setFs(true))}>
 *     Plein écran
 *   </Button>
 *   {fs && <FullscreenOverlay onExit={() => setFs(false)} />}
 */
import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { X, Minimize2 } from 'lucide-react';
import { ClassroomTools } from './ClassroomTools';

/** Active le fullscreen navigateur puis appelle le callback si succès */
export function enterFullscreen(onOk) {
  const el = document.documentElement;
  if (!el.requestFullscreen) {
    if (onOk) onOk();
    return;
  }
  const req = el.requestFullscreen({ navigationUI: 'hide' });
  if (req && req.then) {
    req.then(() => { if (onOk) onOk(); }).catch(() => { if (onOk) onOk(); });
  } else if (onOk) {
    onOk();
  }
}

/**
 * Overlay affiché quand le mode plein écran est actif :
 *   • ajoute la classe body.c2r-fullscreen (CSS globale masque topbar)
 *   • monte les outils Timer/Laser/Annotation
 *   • ferme au ESC ou clic sortie
 */
export function FullscreenOverlay({ onExit }) {
  useEffect(() => {
    document.body.classList.add('c2r-fullscreen');
    // On garde une trace : true dès qu'on est entré en fullscreen une première fois
    // pour éviter que le listener ne close l'overlay si le fullscreen navigateur
    // n'a pas pu s'activer (ex: refus utilisateur ou Playwright headless).
    let hasBeenFullscreen = !!document.fullscreenElement;
    const onFsChange = () => {
      if (document.fullscreenElement) {
        hasBeenFullscreen = true;
      } else if (hasBeenFullscreen) {
        // On était en fullscreen et l'utilisateur a tapé ESC natif → on quitte
        onExit();
      }
    };
    // Écoute aussi ESC directement pour couvrir le cas où on n'a jamais été en fullscreen navigateur
    const onKey = (e) => { if (e.key === 'Escape') onExit(); };
    document.addEventListener('fullscreenchange', onFsChange);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('c2r-fullscreen');
      document.removeEventListener('fullscreenchange', onFsChange);
      window.removeEventListener('keydown', onKey);
      if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [onExit]);

  const handleExit = () => {
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    onExit();
  };

  return (
    <>
      {/* Bouton "Quitter plein écran" */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleExit}
        className="fixed top-3 right-3 z-[10001] bg-black/70 backdrop-blur-md text-white hover:bg-black/85 border border-white/20 h-9 px-3"
        data-testid="fullscreen-exit"
        title="Quitter le plein écran (ESC)"
      >
        <Minimize2 className="w-4 h-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Quitter</span>
      </Button>

      {/* Outils salle : Timer, Laser, Annotation */}
      <ClassroomTools />
    </>
  );
}

export default FullscreenOverlay;
